'use strict';

Object.defineProperty(exports, Symbol.toStringTag, { value: 'Module' });

const React = require('react');
const react = require('@emotion/react');
const styleEncapsulation = require('@zapier/style-encapsulation');
const theme_Colors = require('../../../theme/Colors.cjs.js');
require('../../../theme/Shadows.cjs.js');
const theme_Typography = require('../../../theme/Typography.cjs.js');
const components_display_Badge_Badge = require('../../display/Badge/Badge.cjs.js');
const components_display_Tag_Tag = require('../../display/Tag/Tag.cjs.js');
const components_navigation_Link_Link = require('../Link/Link.cjs.js');
const styleMixins_reset = require('../../../style-mixins/reset.cjs.js');
const components_navigation_Tabs_TabsDropdown = require('./TabsDropdown.cjs.js');

const Styles = styleEncapsulation.labelStyles("Tabs", {
  root: (size) => [
    react.css`
      ${theme_Typography.Typography.SmallPrint1}
      position: relative;
      display: grid;
      grid-template-columns: 1fr auto;
      align-items: center;
    `,
    size === "medium" && styleEncapsulation.withModifier("medium", react.css`
          height: var(--zds-size-medium);
        `),
    size === "large" && styleEncapsulation.withModifier("large", react.css`
          height: var(--zds-size-large);
        `)
  ],
  primary: ({ count }) => [
    react.css`
      ${styleMixins_reset.boxSizing};
      display: grid;
      height: 100%;
      max-width: 100%;
      min-width: 0; // Allow shrinking.
    `,
    // `minmax(auto, min-content)` behaves such that at the min value of
    // `auto` items could truncate if necessary, and at the max value of
    // `min-content` items will not render any wider than they need to,
    // ensuring that they are left-aligned rather than stretching to fill
    // any leftover space.
    styleEncapsulation.withModifier(`count-${count}`, react.css`
        grid-template-columns: repeat(${count}, minmax(auto, min-content));
      `)
  ],
  item: ({ isSelected }) => [
    react.css`
      ${styleMixins_reset.baseReset};
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: var(--zds-space-8);
      text-align: center;
      color: var(--zds-text-default);
      text-decoration: none;
      padding: 0 var(--zds-space-16);

      &:not([disabled]):hover,
      &:not([disabled]):focus {
        color: var(--zds-text-default);
        font: var(--zds-small-print-3);
        outline: none;
      }

      &:not([disabled]):focus-visible {
        outline: var(--zds-border-focus);
      }

      &[disabled] {
        color: var(--zds-text-weakest);
        cursor: not-allowed;
      }
    `,
    isSelected && styleEncapsulation.withModifier("selected", react.css`
          color: var(--zds-text-default);
          font: var(--zds-small-print-3);
        `)
  ],
  itemText: react.css`
    ${styleMixins_reset.boxSizing};
    display: block;
    white-space: nowrap;

    /*
      Applies an invisible, bold version of the text in order to prevent a layout shift when hovering over an item.
      https://css-tricks.com/bold-on-hover-without-the-layout-shift/
    */
    &:after {
      display: block;
      content: attr(data-text);
      content: attr(data-text) / '';
      height: 0;
      visibility: hidden;
      overflow: hidden;
      user-select: none;
      pointer-events: none;
      font-weight: var(--zds-semi-bold-inter);

      @media speech {
        display: none;
      }
    }
  `,
  badgeAndTag: ({ isDisabled = false }) => [
    react.css`
      ${styleMixins_reset.boxSizing};
      font-size: var(--zds-font-size-small-print) !important;
    `,
    isDisabled && react.css`
        background-color: var(--zds-background-stronger) !important;
        color: var(--zds-text-weaker) !important;
      `
  ],
  indicatorTrack: (shouldRenderTrack) => [
    react.css`
      ${styleMixins_reset.boxSizing};
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      /*
        Because there is no 1px size token, we:
        - co-opt zds-size-2 and subtract 1px to get a 1px height for PDL
        - use a 5px fallback and subtract 1px to get a 4px height for legacy
      */
      height: calc(var(--zds-size-2) - 1px);
    `,
    // The track always renders to the DOM to contain the indicator,
    // but it shouldn't always _visibly_ render.
    shouldRenderTrack && styleEncapsulation.withModifier("visible", react.css`
          background-color: ${theme_Colors.Colors.GrayWarm3};
        `)
  ],
  indicator: react.css`
    ${styleMixins_reset.boxSizing};
    position: absolute;
    height: var(--zds-size-4);
    /*
      Again, co-opting --zds-size 2 to generate:
      - a top value of -3px for PDL (so that the indicator's bottom aligns with the bottom of the track)
      - a top value of 0px for legacy (so that the indicator appears within the track)
    */
    top: calc(var(--zds-size-2) - 5px);
    border-radius: inherit;
    background-color: var(--zds-ui-primary);
    transition: all 250ms ease-in-out;

    &[data-type='brand'] {
      background-color: var(--zds-ui-brand);
    }
  `
});
const TabsItem = (props) => {
  if (props.disabled) {
    return react.jsx(
      "button",
      { type: "button", "aria-label": props.ariaLabel, css: Styles.item(props), disabled: true, "data-zds": true },
      react.jsx("span", { css: Styles.itemText, "data-zds": true }, props.text),
      props.badgeText && react.jsx(components_display_Badge_Badge.Badge, { css: Styles.badgeAndTag({
        isDisabled: true
      }), variant: props.badgeVariant }, props.badgeText),
      props.tagText && react.jsx(components_display_Tag_Tag.Tag, { variant: props.tagVariant, css: Styles.badgeAndTag({
        isDisabled: true
      }) }, props.tagText)
    );
  }
  let isSelectedBadgeVariant = props.badgeVariant;
  switch (props.badgeVariant) {
    case "neutral":
      isSelectedBadgeVariant = "neutral-stronger";
      break;
    case "primary":
      isSelectedBadgeVariant = "primary-stronger";
      break;
    case "brand":
      isSelectedBadgeVariant = "brand-stronger";
      break;
    case "success":
      isSelectedBadgeVariant = "success-stronger";
      break;
    case "warning":
      isSelectedBadgeVariant = "warning-stronger";
      break;
    case "error":
      isSelectedBadgeVariant = "error-stronger";
      break;
  }
  return react.jsx(
    components_navigation_Link_Link.Link,
    { "aria-label": props.ariaLabel, "aria-current": props.isSelected ? props.ariaCurrent : void 0, color: null, css: Styles.item(props), href: props.href, onClick: props.onChoose, ref: props.forwardedRef },
    react.jsx("span", { css: Styles.itemText, "data-text": props.text, "data-zds": true }, props.text),
    props.badgeText && react.jsx(components_display_Badge_Badge.Badge, { css: Styles.badgeAndTag({
      type: props.type
    }), variant: props.isSelected ? isSelectedBadgeVariant : props.badgeVariant }, props.badgeText),
    props.tagText && react.jsx(components_display_Tag_Tag.Tag, { variant: props.tagVariant, css: Styles.badgeAndTag({
      type: props.type
    }) }, props.tagText)
  );
};
const Tabs = React.forwardRef(({ items, selectedItemHref, "aria-label": ariaLabel = void 0, "aria-current": ariaCurrent = "page", isItemSelected = (item, selectedItemHref2) => item.href === selectedItemHref2, onChooseItem = () => {
}, shouldRenderTrack = true, size = "large", type = "default" }, ref) => {
  const selectedItemRef = React.useRef(null);
  const [indicatorStyles, setIndicatorStyles] = React.useState({});
  const shouldRenderIndicator = !items.every((i) => i.isExtra);
  const primaryItems = items.filter((item) => !item.isExtra);
  const primaryLinks = primaryItems.map((item) => {
    const isSelected = isItemSelected(item, selectedItemHref);
    return react.jsx(TabsItem, Object.assign({}, item, { ariaCurrent, forwardedRef: isSelected && shouldRenderIndicator ? selectedItemRef : null, isSelected, key: item.href, onChoose: (event) => onChooseItem(item, event), type }));
  });
  const secondaryItems = items.filter((item) => item.isExtra);
  const isSecondaryItemSelected = secondaryItems.some((item) => {
    return isItemSelected(item, selectedItemHref);
  });
  const secondaryMenu = secondaryItems.length > 0 && react.jsx(
    "div",
    { ref: isSecondaryItemSelected && shouldRenderIndicator ? selectedItemRef : null, "data-zds": true },
    react.jsx(components_navigation_Tabs_TabsDropdown.TabsDropdown, { size, items: secondaryItems, onChooseItem, selectedItemHref })
  );
  React.useEffect(() => {
    const requestId = requestAnimationFrame(() => {
      setIndicatorStyles(selectedItemRef.current ? {
        left: selectedItemRef.current.offsetLeft,
        width: selectedItemRef.current.offsetWidth
      } : {});
    });
    return () => cancelAnimationFrame(requestId);
  }, [items, selectedItemHref]);
  return react.jsx(
    "nav",
    { "aria-label": ariaLabel, css: Styles.root(size), "data-zds": true },
    react.jsx("div", { css: Styles.primary({ count: primaryLinks.length }), ref, "data-zds": true }, primaryLinks),
    secondaryMenu,
    react.jsx(
      "div",
      { css: Styles.indicatorTrack(shouldRenderTrack), "data-zds": true },
      react.jsx("div", { css: Styles.indicator, "data-type": type, style: indicatorStyles, "data-zds": true })
    )
  );
});

exports.Tabs = Tabs;
//# sourceMappingURL=Tabs.cjs.js.map
